import { Box, Divider, Paper, Typography, useTheme } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import type { Annotation } from '@verantos-dev/core-annotation-tool';
import { useAppDispatch, useAppSelector } from '@verantos-dev/core-annotation-tool';
import { selectBatchId } from '@verantos-dev/feature-supervisor-queue';
import { Button } from '@verantos-dev/shared-components';
import { ConfirmRejectModal, ErrorAlertCard } from '@verantos-dev/ui-annotation-tool';

import {
  useGetSupervisorWorkspaceQuery,
  useRejectConflictAnnotationMutation,
  useSupervisorRejectAndCreateAnnotationMutation,
} from '../../../../api/supervisorApiSlice';
import { useSupervisorMode } from '../../../../context/SupervisorModeContext';
import { selectConflictAcceptError, selectConflictRejectError } from '../../../../state/selector';
import {
  addToConflictIdsInReplaceMode,
  clearConflictAcceptError,
  clearConflictRejectError,
  setConflictRejectError,
} from '../../../../state/supervisorSlice';
import { Conflict } from '../../../../types/response';
import { AnnotatorAnnotationCard } from '../AnnotatorTab/AnnotatorAnnotationCard';
import { AnnotatorEvidencesList } from '../EditAndAddAnnotations/AnnotatorEvidencesList';
import { ReferenceTextSection } from '../ReferenceTextSection';
import { AcceptAnnotationModal } from './AcceptAnnotationModal';

interface ConflictCardProps {
  conflict: Conflict;
  conceptId: string;
}

export const ConflictCard = ({ conflict, conceptId }: ConflictCardProps) => {
  const theme = useTheme();
  const { encounterId } = useParams();
  const batchId = useAppSelector(selectBatchId);
  const [rejectConflictAnnotation, { isLoading: isRejectConflictAnnotationLoading }] =
    useRejectConflictAnnotationMutation();
  const [rejectAndCreateAnnotation, { isLoading: isRejectBothLoading }] =
    useSupervisorRejectAndCreateAnnotationMutation();
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showRejectBothModal, setShowRejectBothModal] = useState<boolean>(false);
  const [showAcceptModal, setShowAcceptModal] = useState<boolean>(false);
  const [annotatorNumber, setAnnotatorNumber] = useState<number | null>(null);
  const [annotatorNumberToReject, setAnnotatorNumberToReject] = useState<number | null>(null);
  const [annotationIdToAccept, setAnnotationIdToAccept] = useState<string>('');
  const [annotationId, setAnnotationId] = useState<string>('');
  const [annotationIdToReject, setAnnotationIdToReject] = useState<string>('');
  const acceptError = useAppSelector(selectConflictAcceptError(conflict.conflictId));
  const rejectError = useAppSelector(selectConflictRejectError(conflict.conflictId));
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const { isReadOnly } = useSupervisorMode();
  const errorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if ((acceptError || rejectError) && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [acceptError, rejectError]);

  const handleAcceptAnnotation = async (
    annotationIdToAccept: string,
    annotationIdToReject = '',
    annotatorNumber: number | null = null
  ) => {
    if (!annotationIdToAccept) return;
    setAnnotatorNumber(annotatorNumber);
    setAnnotatorNumberToReject(annotatorNumber === 1 ? 2 : annotatorNumber === 2 ? 1 : null);
    setShowAcceptModal(true);
    setAnnotationIdToAccept(annotationIdToAccept);
    setAnnotationIdToReject(annotationIdToReject);
  };

  const supervisorWorkspace = useGetSupervisorWorkspaceQuery({
    encounterId: encounterId as string,
    batchId: batchId as string,
  }).data;

  const concept = supervisorWorkspace?.data.annotationsReview.find(
    (c: any) => c.conceptId === conceptId
  );

  const onConfirmDelete = async () => {
    try {
      dispatch(setConflictRejectError({ conflictId: conflict.conflictId, error: null }));
      await rejectConflictAnnotation({
        encounterId: encounterId as string,
        batchId: batchId as string,
        annotationIdToReject: annotationId,
      }).unwrap();
    } catch (err: any) {
      console.error('Failed to reject conflict annotation:', err);
      const message =
        err?.data?.detail ||
        err?.data?.message ||
        err?.data?.errorMessage ||
        err?.message ||
        'Failed to reject annotation';
      dispatch(
        setConflictRejectError({
          conflictId: conflict.conflictId,
          error: message,
        })
      );
    }
  };

  const handleRejectAnnotation = (annotationIdToReject: string) => {
    const remainingAnnotations = [
      conflict.annotator1Annotation?.annotationId,
      conflict.annotator2Annotation?.annotationId,
    ].filter((id): id is string => !!id);

    const isLastAnnotation = remainingAnnotations.length === 1;
    const hasResolvedInConcept =
      concept?.conflicts?.some((c: any) => c.resolved?.annotations?.length > 0) ||
      (concept?.supervisorAnnotations?.length ?? 0) > 0;
    const hasMatchingInConcept = (concept?.matchingAnnotation?.length ?? 0) > 0;
    const hasOtherConflictAnnotations = concept?.conflicts?.some(
      (c: any) =>
        c.conflictId !== conflict.conflictId &&
        (c.annotator1Annotation?.annotationId || c.annotator2Annotation?.annotationId)
    );

    if (
      isLastAnnotation &&
      !hasResolvedInConcept &&
      !hasMatchingInConcept &&
      !hasOtherConflictAnnotations
    ) {
      enableAddNewAnnotationModeToRejectAnnotations();
      return;
    }

    setAnnotationId(annotationIdToReject);
    setShowDeleteModal(true);
  };

  const handleRejectBoth = () => {
    const hasResolvedInConcept =
      concept?.conflicts?.some((c: any) => c.resolved?.annotations?.length > 0) ||
      (concept?.supervisorAnnotations?.length ?? 0) > 0;
    const hasMatchingInConcept = (concept?.matchingAnnotation?.length ?? 0) > 0;
    const hasOtherConflictAnnotations = concept?.conflicts?.some(
      (c: any) =>
        c.conflictId !== conflict.conflictId &&
        (c.annotator1Annotation?.annotationId || c.annotator2Annotation?.annotationId)
    );

    if (!hasResolvedInConcept && !hasMatchingInConcept && !hasOtherConflictAnnotations) {
      enableAddNewAnnotationModeToRejectAnnotations();
    } else {
      setShowRejectBothModal(true);
    }
  };

  const onConfirmRejectBoth = async () => {
    try {
      dispatch(setConflictRejectError({ conflictId: conflict.conflictId, error: null }));

      const annotationIdsToReject = [
        conflict.annotator1Annotation?.annotationId,
        conflict.annotator2Annotation?.annotationId,
      ].filter((id): id is string => !!id);

      await rejectAndCreateAnnotation({
        encounterId: encounterId as string,
        conflictId: conflict.conflictId,
        conceptId,
        annotationIdsToReject,
        attributes: [],
        spans: [],
      }).unwrap();
    } catch (err: any) {
      console.error('Failed to reject both conflict annotations:', err);
      const message =
        err?.data?.detail ||
        err?.data?.message ||
        err?.data?.errorMessage ||
        err?.message ||
        'Failed to reject annotations';
      dispatch(setConflictRejectError({ conflictId: conflict.conflictId, error: message }));
    } finally {
      setShowRejectBothModal(false);
    }
  };

  const bothAnnotationsPresent =
    conflict.annotator1Annotation !== null && conflict.annotator2Annotation !== null;

  // independent (any conflictType): both annotations shown, each with their own Accept + Reject buttons.
  // pairwise: both annotations shown with Accept A1 / Accept A2; accepting one always rejects the other.
  // known_vs_known is always independent.
  const isPairWise = conflict.viewType === 'pairwise';
  const rejectOtherOnAccept = isPairWise;
  const isIndependentBothAnnotations = !isPairWise && bothAnnotationsPresent;

  const enableAddNewAnnotationModeToRejectAnnotations = () => {
    const annotationIdsToReject = [
      conflict.annotator1Annotation?.annotationId,
      conflict.annotator2Annotation?.annotationId,
    ].filter((id): id is string => !!id);
    dispatch(
      addToConflictIdsInReplaceMode({ conflictId: conflict.conflictId, annotationIdsToReject })
    );
  };

  return (
    <Box
      id={`single-conflict-card-${conflict.conflictId}`}
      sx={{
        border: `1px solid ${theme.palette.grey[300]}`,
        p: theme.spacing(4),
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ display: 'flex', gap: theme.spacing(4), flexDirection: 'column' }}>
        {conflict.annotator1Annotation && (
          <Paper
            sx={{
              borderRadius: 0,
              backgroundColor: theme.palette.grey[100],
              padding: theme.spacing(4),
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              borderLeft: `3px solid ${theme.palette.error.main}`,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', gap: theme.spacing(2), alignItems: 'center' }}>
                <Typography variant="body1">
                  {formatMessage({ id: 'supervisorReview.conflictTab.annotator1' })}
                </Typography>
                {conflict.annotator1Annotation.spans.length === 0 && (
                  <ReferenceTextSection
                    referenceText={conflict.annotator1Annotation.referenceText}
                  />
                )}
              </Box>
              <AnnotatorAnnotationCard annotation={conflict.annotator1Annotation as Annotation} />
              <AnnotatorEvidencesList
                annotation={conflict.annotator1Annotation as Annotation}
                conceptId={conceptId}
                editingAnnotationId={conflict.annotator1Annotation.annotationId}
                restrictEdit
              />
            </Box>

            {!isReadOnly && bothAnnotationsPresent && isPairWise && (
              <Box
                sx={{
                  display: 'flex',
                  gap: theme.spacing(4),
                  pt: theme.spacing(2),
                  justifyContent: 'center',
                }}
              >
                <Button
                  id={`accept-button-annotator1-${conflict.conflictId}`}
                  displayLabel="Accept A1"
                  variant="contained"
                  onClickHandler={() =>
                    handleAcceptAnnotation(
                      conflict.annotator1Annotation?.annotationId ?? '',
                      rejectOtherOnAccept ? conflict.annotator2Annotation?.annotationId ?? '' : '',
                      1
                    )
                  }
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    textTransform: 'none',
                    fontSize: '14px',
                    fontWeight: 500,
                  }}
                />
              </Box>
            )}
            {!isReadOnly && isIndependentBothAnnotations && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: theme.spacing(4),
                  pt: theme.spacing(2),
                }}
              >
                <Button
                  id={`accept-button-annotator1-${conflict.conflictId}`}
                  displayLabel="Accept"
                  variant="contained"
                  onClickHandler={() =>
                    handleAcceptAnnotation(conflict.annotator1Annotation?.annotationId ?? '', '', 1)
                  }
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    textTransform: 'none',
                    fontSize: '14px',
                    fontWeight: 500,
                  }}
                />
                <Button
                  id={`reject-button-annotator1-${conflict.conflictId}`}
                  displayLabel={formatMessage({ id: 'supervisorReview.conflictTab.button.reject' })}
                  variant="contained"
                  onClickHandler={() =>
                    handleRejectAnnotation(conflict.annotator1Annotation?.annotationId ?? '')
                  }
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    textTransform: 'none',
                    fontSize: '14px',
                    fontWeight: 500,
                  }}
                />
              </Box>
            )}
          </Paper>
        )}

        {conflict.annotator2Annotation && (
          <Paper
            sx={{
              borderRadius: 0,
              backgroundColor: theme.palette.grey[100],
              padding: theme.spacing(4),
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              borderLeft: `3px solid ${theme.palette.error.main}`,
            }}
          >
            <Box sx={{ display: 'flex', gap: theme.spacing(2), alignItems: 'center' }}>
              <Typography variant="body1">
                {formatMessage({ id: 'supervisorReview.conflictTab.annotator2' })}
              </Typography>
              {conflict.annotator2Annotation.spans.length === 0 && (
                <ReferenceTextSection referenceText={conflict.annotator2Annotation.referenceText} />
              )}
            </Box>

            <AnnotatorAnnotationCard annotation={conflict.annotator2Annotation as Annotation} />
            <AnnotatorEvidencesList
              annotation={conflict.annotator2Annotation as Annotation}
              conceptId={conceptId}
              editingAnnotationId={conflict.annotator2Annotation.annotationId}
              restrictEdit
            />

            {!isReadOnly && bothAnnotationsPresent && isPairWise && (
              <Box
                sx={{
                  display: 'flex',
                  gap: theme.spacing(4),
                  pt: theme.spacing(2),
                  justifyContent: 'center',
                }}
              >
                <Button
                  id={`accept-button-annotator2-${conflict.conflictId}`}
                  displayLabel="Accept A2"
                  variant="contained"
                  onClickHandler={() =>
                    handleAcceptAnnotation(
                      conflict.annotator2Annotation?.annotationId ?? '',
                      rejectOtherOnAccept ? conflict.annotator1Annotation?.annotationId ?? '' : '',
                      2
                    )
                  }
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    textTransform: 'none',
                    fontSize: '14px',
                    fontWeight: 500,
                  }}
                />
              </Box>
            )}
            {!isReadOnly && isIndependentBothAnnotations && (
              <Box
                sx={{
                  display: 'flex',
                  gap: theme.spacing(4),
                  justifyContent: 'center',
                  pt: theme.spacing(2),
                }}
              >
                <Button
                  id={`accept-button-annotator2-${conflict.conflictId}`}
                  displayLabel="Accept"
                  variant="contained"
                  onClickHandler={() =>
                    handleAcceptAnnotation(conflict.annotator2Annotation?.annotationId ?? '', '', 2)
                  }
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    textTransform: 'none',
                    fontSize: '14px',
                    fontWeight: 500,
                  }}
                />
                <Button
                  id={`reject-button-annotator2-${conflict.conflictId}`}
                  displayLabel={formatMessage({ id: 'supervisorReview.conflictTab.button.reject' })}
                  variant="contained"
                  onClickHandler={() =>
                    handleRejectAnnotation(conflict.annotator2Annotation?.annotationId ?? '')
                  }
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    textTransform: 'none',
                    fontSize: '14px',
                    fontWeight: 500,
                  }}
                />
              </Box>
            )}
          </Paper>
        )}
      </Box>
      <Divider sx={{ mt: theme.spacing(4) }} />

      <Box ref={errorRef}>
        <ErrorAlertCard
          errorMessage={acceptError ?? ''}
          onDismiss={() => dispatch(clearConflictAcceptError(conflict.conflictId))}
        />
        <ErrorAlertCard
          errorMessage={rejectError ?? ''}
          onDismiss={() => dispatch(clearConflictRejectError(conflict.conflictId))}
        />
      </Box>

      <Box id="conflict-button-section">
        {isReadOnly ? null : isIndependentBothAnnotations ? null : isPairWise ? (
          <Box
            sx={{
              display: 'flex',
              gap: theme.spacing(4),
              justifyContent: 'center',
              pt: theme.spacing(4),
            }}
          >
            <Button
              id={`reject-both-button-${conflict.conflictId}`}
              displayLabel={formatMessage({ id: 'supervisorReview.conflictTab.button.rejectBoth' })}
              variant="outlined"
              onClickHandler={handleRejectBoth}
              sx={{
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 500,
              }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: theme.spacing(4),
              pt: theme.spacing(4),
            }}
          >
            <Button
              id={`accept-button-${conflict.conflictId}`}
              displayLabel="Accept"
              variant="contained"
              onClickHandler={() =>
                handleAcceptAnnotation(
                  conflict.annotator2Annotation?.annotationId ??
                    conflict.annotator1Annotation?.annotationId ??
                    ''
                )
              }
              sx={{
                backgroundColor: theme.palette.primary.main,
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 500,
              }}
            />
            <Button
              id={`reject-button-${conflict.conflictId}`}
              displayLabel={formatMessage({ id: 'supervisorReview.conflictTab.button.reject' })}
              variant="contained"
              onClickHandler={() =>
                handleRejectAnnotation(
                  conflict.annotator2Annotation?.annotationId ??
                    conflict.annotator1Annotation?.annotationId ??
                    ''
                )
              }
              sx={{
                backgroundColor: theme.palette.primary.main,
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 500,
              }}
            />
          </Box>
        )}
      </Box>

      <ConfirmRejectModal
        showModal={showDeleteModal}
        setShowModal={setShowDeleteModal}
        itemLabel="annotation"
        onConfirm={onConfirmDelete}
        isLoading={isRejectConflictAnnotationLoading}
      />
      <ConfirmRejectModal
        showModal={showRejectBothModal}
        setShowModal={setShowRejectBothModal}
        itemLabel="both annotations"
        onConfirm={onConfirmRejectBoth}
        isLoading={isRejectBothLoading}
      />
      <AcceptAnnotationModal
        showModal={showAcceptModal}
        setShowModal={setShowAcceptModal}
        conflictId={conflict.conflictId}
        annotatorNumber={annotatorNumber}
        annotatorNumberToReject={annotatorNumberToReject}
        annotationIdToAccept={annotationIdToAccept}
        annotationIdToReject={annotationIdToReject}
      />
    </Box>
  );
};
